<template>
  <div class="browserInPage">
    <el-alert
      :title="$t('loginUrlBrowser.alert')"
      type="info"
      class="add-account-button-alert"
    >
    </el-alert>
    <div class="browserHeader">
      <div class="browserTitle">{{ $t('loginUrlBrowser.title') }}</div>
    </div>
    <div
      class="browserWrapper"
      element-loading-background="rgb(255, 255, 255)"
      v-loading="loading"
    >
      <iframe
        class="iframe"
        ref="iframe"
        :src="url"
        frameborder="0"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
    <div class="account-section">
      <div class="accountTitle">{{ $t('loginUrlBrowser.accounts') }}</div>
      <!-- Table Header -->
      <div class="account-header">
        <div class="header-item">{{ $t('loginUrlBrowser.startUrl') }}</div>
        <div class="header-item">{{ $t('loginUrlBrowser.role') }}</div>
        <div class="header-item">{{ $t('loginUrlBrowser.actions') }}</div>
      </div>

      <!-- Account List -->
      <ul class="accountList">
        <li
          v-for="(account, index) in accounts"
          :key="account.targetId"
          class="accountItem"
        >
          <el-form
            :model="account"
            :rules="rules"
            ref="accountForm"
            :key="`form-${index}`"
          >
            <div class="form-row">
              <!-- Login URL -->
              <el-form-item
                prop="startUrl"
                class="url-input"
              >
                <el-input
                  class="input-item"
                  size="medium"
                  readonly
                  :value="account.startUrl"
                  placeholder="https://example.com/login"
                  disabled
                ></el-input>
              </el-form-item>

              <!-- Role -->
              <el-form-item
                prop="role"
                class="role-select"
              >
                <el-select
                  size="medium"
                  class="role-selector"
                  v-model="account.role"
                  allow-create
                  :placeholder="$t('loginUrlBrowser.roleSelectPlaceholder')"
                  :disabled="account.saved"
                >
                  <el-option
                    v-for="item in options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  >
                  </el-option>
                </el-select>
              </el-form-item>

              <!-- Actions -->
              <div class="actions">
                <el-button
                  class="action-button"
                  :type="account.session ? 'success' : 'warning'"
                  size="small"
                  :loading="saveLoadingIndex === index"
                  @click="handleSaveSession(account, index)"
                >
                  <i
                    v-if="saveLoadingIndex !== index"
                    :class="[
                      account.session ? 'el-icon-circle-check' : 'el-icon-time',
                      'el-icon--left',
                      'action-icon'
                    ]"
                  />
                  {{ $t('loginUrlBrowser.verifyLogin') }}
                </el-button>
                <el-button
                  class="action-button"
                  size="small"
                  type="danger"
                  @click="deleteAccount(account, index)"
                >
                  <i class="el-icon-delete el-icon--left action-icon" />
                  {{ $t('loginUrlBrowser.deleteAccount') }}
                </el-button>
              </div>
            </div>
          </el-form>
        </li>
      </ul>

      <!-- Add Account Button -->
      <div class="add-account">
        <el-tooltip
          effect="dark"
          :content="$t('loginUrlBrowser.addAccountDisabledTip')"
          placement="top"
          :disabled="!!canAddNewAccount"
          popper-class="add-account-button-tooltip"
        >
          <el-button
            type="primary"
            size="medium"
            icon="el-icon-plus"
            :loading="addAccountLoading"
            @click="addAccount"
            :disabled="!canAddNewAccount"
          >
            {{ $t('loginUrlBrowser.addAccount') }}
          </el-button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { MyPromise } from '@/utils/my-promise';
import { RoleOptions, RemoteBrowserUrlKey } from '@/enum';
import { getRemoteBrowserLink } from '@/api/base';
import { storage } from '@/utils/storage';

const DefaultTabUrl = ['about:blank'];
const OutOfDateTimeout = 4 * 60 * 1000;

const validateUrl = (rule, value, callback) => {
  try {
    new URL(value);
    callback();
  } catch (err) {
    callback(new Error('Please enter a valid URL'));
  }
};

export default {
  props: {
    payload: Object,
  },
  data() {
    const { serviceUrl = '', accounts } = this.payload || {};
    const url = storage.get(RemoteBrowserUrlKey) || '';

    return {
      serviceUrl,
      url,
      accounts: accounts || [],
      options: RoleOptions,
      rules: {
        startUrl: [
          { required: true, message: 'Please enter login URL', trigger: 'blur' },
          { validator: validateUrl, trigger: 'blur' },
        ],
        role: [{ required: true, message: 'Please select role', trigger: 'change' }],
      },
      iframeMessageHandlers: {}, // Store message handlers
      tabs: [],
      iframeLoadedPromise: new MyPromise(),
      saveLoadingIndex: -1,
      loading: true,
      addAccountLoading: false,
    };
  },
  watch: {
    accounts: {
      immediate: true,
      handler(val) {
        const isAllSaved = val?.length > 0 ? val.every((account) => account.session) : false;

        if (isAllSaved) {
          this.$emit('next-disabled', false);
        } else {
          this.$emit('next-disabled', true);
        }
      },
    },
  },
  computed: {
    canAddNewAccount() {
      if (this.loading) {
        return false;
      }
      return this.accounts.length === 0 || this.accounts[this.accounts.length - 1].session;
    },
  },
  methods: {
    async addAccount() {
      this.addAccountLoading = true;
      let targetUrl = this.serviceUrl || DefaultTabUrl[0];

      if (this.accounts.length > 0) {
        targetUrl = this.accounts[this.accounts.length - 1].startUrl;
      }

      await this.createNewContextTab(targetUrl);

      setTimeout(() => {
        this.initAccounts();
        this.addAccountLoading = false;
      }, 1000);
    },
    async deleteAccount(account, index) {
      try {
        await this.$confirm(this.$t('loginUrlBrowser.deleteConfirmMessage'), this.$t('loginUrlBrowser.deleteConfirmTitle'), {
          confirmButtonText: this.$t('loginUrlBrowser.deleteConfirmOk'),
          cancelButtonText: this.$t('loginUrlBrowser.deleteConfirmCancel'),
          type: 'warning',
        });

        if (this.accounts.length === 1) {
          await this.clearSession(account);
          setTimeout(() => {
            this.initAccounts();
          }, 1000);
        }

        await this.closeTab(account.targetId);
        this.accounts.splice(index, 1);
      } catch (e) {
        console.log('Deletion canceled');
      }
    },

    async handleSaveSession(account, index) {
      try {
        // Add form validation before proceeding
        const form = this.$refs.accountForm[index];
        const valid = await form.validate();
        if (!valid) {
          return;
        }

        const roleItem = RoleOptions.find((item) => item.value === account.role);

        // Add confirmation dialog
        try {
          await this.$confirm(this.$t('loginUrlBrowser.loginConfirmMessage', { role: roleItem?.label }), this.$t('loginUrlBrowser.loginConfirmTitle'), {
            confirmButtonText: this.$t('loginUrlBrowser.loginConfirmOk'),
            cancelButtonText: this.$t('loginUrlBrowser.loginConfirmCancel'),
            type: 'warning',
          });
        } catch (e) {
          console.log('Login verification canceled');
          return;
        }

        this.saveLoadingIndex = index;
        const { sessionInfo } = await this.getSession(account.targetId);
        this.accounts.splice(index, 1, {
          ...account,
          session: sessionInfo
            ? {
                ...sessionInfo,
                headers: sessionInfo.headers.map((header) => {
                  return {
                    url: header.url,
                    method: header.method,
                    requestHeaders: Object.keys(header.requestHeaders).map((key) => {
                      return {
                        name: key,
                        value: header.requestHeaders[key],
                      };
                    }),
                  };
                }),
              }
            : null,
        });
        this.saveLoadingIndex = -1;
        const role = RoleOptions.find((item) => item.value === account.role);
        this.$message({
          message: this.$t('loginUrlBrowser.verifySuccess', { role: role.label }),
          type: 'success',
          offset: 200,
        });
      } catch (e) {
        console.log(e);
        this.saveLoadingIndex = -1;
      }
    },
    // Send message to iframe and return promise for response

    async initAccounts(newTabs) {
      try {
        const tabs = newTabs || (await this.getTabs());

        if (tabs.length === 0 && !newTabs) {
          const url = this.serviceUrl || '';
          await this.createNewTab(url);
          this.accounts = [this.createDefaultAccount];
          return;
        }

        const currentAccounts = this.accounts;
        const accounts = tabs.map((tab) => {
          const currentAccount = currentAccounts.find((account) => account.targetId === tab.targetId) || this.createDefaultAccount();
          const tabUrl = DefaultTabUrl.includes(tab.url) ? '' : tab.url;

          return {
            ...currentAccount,
            startUrl: tabUrl,
            targetId: tab.targetId,
            browserContextId: tab.browserContextId,
          };
        });

        const isDefaultTabUrl = tabs.every((tab) => DefaultTabUrl.includes(tab.url));

        if (isDefaultTabUrl) {
          await this.go(this.serviceUrl || DefaultTabUrl[0]);
        }

        this.accounts = accounts;
      } catch (e) {
        console.log(e);
      }
    },

    async clearSession(account) {
      try {
        if (!account.targetId) {
          this.$message.warning('No active session to clear');
          return;
        }

        await this.sendIframeMessage('clearSession', {
          targetId: account.targetId,
        });

        this.$message.success('Session cleared successfully');
      } catch (error) {
        console.error('Error clearing session:', error);
        this.$message.error('Failed to clear session');
      }
    },

    // Example methods to interact with iframe
    async getTabs() {
      return await this.sendIframeMessage('getTabs');
    },

    async createNewTab(url) {
      return await this.sendIframeMessage('newTab', { url });
    },

    async createNewContextTab(url) {
      return await this.sendIframeMessage('newContextTab', { url });
    },

    async switchTab(targetId) {
      return await this.sendIframeMessage('switchTab', { targetId });
    },

    async closeTab(targetId) {
      return await this.sendIframeMessage('closeTab', { targetId });
    },

    async getSession(targetId) {
      return await this.sendIframeMessage('getSession', { targetId });
    },

    async go(url) {
      return await this.sendIframeMessage('go', { url });
    },

    handleIframeMessage(event) {
      const { type, data, messageId } = event.data;

      if (!type) {
        return;
      }

      if (type === 'iframeLoaded') {
        this.iframeLoadedPromise.resolve();
        return;
      }

      if (type === 'updateTabs') {
        this.initAccounts(data.data);
        return;
      }

      if (messageId && this.iframeMessageHandlers[messageId]) {
        this.iframeMessageHandlers[messageId](data);
      }
    },

    sendIframeMessage(type, data = null) {
      return new Promise((resolve, reject) => {
        const messageId = Date.now().toString();

        // Store handler for this message
        this.iframeMessageHandlers[messageId] = (response) => {
          if (response.success) {
            resolve(response.data);
          } else {
            reject(response.error || 'Failed to send message to iframe');
          }
          delete this.iframeMessageHandlers[messageId];
        };

        // Send message to iframe
        this.$refs.iframe.contentWindow.postMessage(
          {
            type,
            data,
            messageId,
          },
          '*'
        );
      });
    },

    createDefaultAccount() {
      return {
        startUrl: '',
        role: '',
        targetId: Math.random(),
        session: null,
        browserContextId: '',
      };
    },

    next() {
      return {
        stage: 'LoginUrlBrowser',
        data: {
          accounts: this.accounts,
        },
      };
    },
  },
  created() {
    const url = storage.get(RemoteBrowserUrlKey);

    this.iframeLoadedPromise.promise.then(async () => {
      if (!url) {
        await this.sendIframeMessage('clearTabs');
      }

      this.initAccounts();

      setTimeout(() => {
        this.loading = false;
      }, 1000);
    });

    if (url) {
      storage.set(RemoteBrowserUrlKey, url, OutOfDateTimeout);
      return;
    }

    getRemoteBrowserLink().then((res) => {
      this.url = res.url;
      storage.set(RemoteBrowserUrlKey, this.url, OutOfDateTimeout);
    });
  },
  mounted() {
    window.addEventListener('message', this.handleIframeMessage);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleIframeMessage);
  },
};
</script>

<style lang="scss">
.browserInPage {
  margin-top: -40px;
  height: 100%;

  .role-selector {
    display: block;
  }

  .add-account-button-alert {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .browserTitle,
  .accountTitle {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  .browserWrapper {
    overflow: hidden;
    border-radius: 8px;
    font-size: 0;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 4px 8px;
  }

  .iframe {
    width: 100%;
    height: 500px;
  }
}

.account-section {
  padding-top: 20px;
}

.account-header {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  margin-bottom: 10px;
  gap: 20px;

  .header-item {
    font-size: 14px;
    overflow: hidden;

    &:nth-child(1) {
      flex: 2;
    }
    &:nth-child(2) {
      flex: 1;
    }
    &:nth-child(3) {
      flex: 1;
    }
  }
}

.input-item .el-input__inner {
  border: 1px solid #dcdfe6 !important;
}

.accountList {
  list-style: none;
  padding: 0;
  margin: 0;
}

.accountItem {
  margin-bottom: 15px;
  overflow: hidden;

  .form-row {
    display: flex;
    align-items: center;
    gap: 20px;

    .url-input {
      flex: 2;
      margin-bottom: 0;
    }

    .role-select {
      flex: 1;
      margin-bottom: 0;
    }

    .actions {
      flex: 1;
      display: flex;

      .action-button {
        position: relative;
        bottom: 1px;
        flex: 1;
      }
    }
  }
}

.add-account {
  margin-top: 20px;
}

.action-icon {
  font-size: 16px;
  color: #fff;
  position: relative;
  top: 2px;
}

.add-account-button-tooltip {
  word-break: break-word;
}
</style>
